/**
 * Bundle Size Guide controller.
 *
 * Adds the in-place Size Guide drawer (same as the PDP) to each product inside a bundle.
 * Self-contained: does NOT depend on product-info.js or a <product-info> element, because
 * the bundle page does not load them. Uses event delegation on document so it keeps working
 * after the bundle's AJAX color-swap replaces a product snippet's innerHTML.
 *
 * Each included product renders its own `fitting-modal` (class .ProductTabs-fittingModal)
 * inside its `.js-smp-bundle-snippet-container`, plus a trigger `.js-bundle-size-guide-trigger`
 * carrying `data-size-guide-type`. Behavior mirrors product-info.js `toggleSizeGuide`.
 */
(function () {
  if (window.__bundleSizeGuideInit) return;
  window.__bundleSizeGuideInit = true;

  const SIZE_GUIDES_URL = '/pages/size-guides';
  let sizeGuidesDocPromise = null;

  // Fetch + parse /pages/size-guides once, then reuse across products/opens.
  function getSizeGuidesDoc() {
    if (!sizeGuidesDocPromise) {
      sizeGuidesDocPromise = fetch(SIZE_GUIDES_URL)
        .then((r) => r.text())
        .then((html) => new DOMParser().parseFromString(html, 'text/html'))
        .catch((err) => {
          // Allow a later retry if the fetch failed.
          sizeGuidesDocPromise = null;
          throw err;
        });
    }
    return sizeGuidesDocPromise;
  }

  function openModal(trigger) {
    const scope = trigger.closest('.js-smp-bundle-snippet-container') || document;
    const modal = scope.querySelector('.ProductTabs-fittingModal');
    if (!modal) return;

    const type = trigger.dataset.sizeGuideType;
    if (!type) return;

    modal.classList.add('is-active');
    modal.classList.remove('is-closed');
    document.body.style.overflow = 'hidden';

    const container = modal.querySelector('.js-fittings-modal-content');
    const titleEl = modal.querySelector('[data-size-chart-title]');

    getSizeGuidesDoc()
      .then((doc) => {
        const source = doc.getElementById(type);
        if (!source) {
          console.error(`Size guide element with ID ${type} not found in ${SIZE_GUIDES_URL}`);
          return;
        }
        if (container) {
          container.innerHTML = source.innerHTML;
          const content = container.querySelector('.size-guide__content');
          if (content) content.dataset.initialized = '';
        }
        if (titleEl) {
          const titleInput = doc.getElementById('size-guide-pdp-drawer-dropdown-title');
          if (titleInput) titleEl.innerHTML = titleInput.value;
        }
        initScrollbar(modal);
      })
      .catch((error) => console.error('Error loading size guide:', error));
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-active');
    document.body.style.overflow = 'auto';
  }

  // Mobile horizontal scrollbar for wide tables — scoped to a single modal instance.
  function initScrollbar(modal) {
    const scrollbar = modal.querySelector('.size-guide__scrollbar');
    const container = modal.querySelector('.js-fittings-modal-content .size-guide__content');
    if (!scrollbar || !container) return;
    if (container.dataset.initialized === 'true') return;
    container.dataset.initialized = 'true';

    const track = scrollbar.querySelector('.size-guide__scrollbar-track');
    const thumb = scrollbar.querySelector('.size-guide__scrollbar-thumb');
    if (!track || !thumb) return;

    let isDragging = false;
    let dragStartX = 0;
    let thumbStartLeft = 0;

    function updateThumb() {
      const canScroll = container.scrollWidth > container.clientWidth;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      scrollbar.style.display = canScroll && isMobile ? 'block' : 'none';
      if (!canScroll || !isMobile) return;

      const trackWidth = track.clientWidth;
      const contentRatio = container.clientWidth / container.scrollWidth;
      const minThumbPx = 24;
      const thumbWidth = Math.max(Math.round(trackWidth * contentRatio), minThumbPx);
      const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);

      const scrollRatio = container.scrollLeft / (container.scrollWidth - container.clientWidth);
      const leftPx = Math.round(scrollRatio * maxThumbLeft);

      thumb.style.width = thumbWidth + 'px';
      thumb.style.transform = 'translateX(' + leftPx + 'px)';
    }

    function setScrollFromThumb(leftPx) {
      const trackWidth = track.clientWidth;
      const thumbWidth = thumb.clientWidth;
      const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);
      const clampedLeft = Math.max(0, Math.min(leftPx, maxThumbLeft));
      const scrollRatio = maxThumbLeft > 0 ? clampedLeft / maxThumbLeft : 0;
      const maxScroll = container.scrollWidth - container.clientWidth;
      container.scrollLeft = Math.round(scrollRatio * maxScroll);
    }

    container.addEventListener('scroll', updateThumb, { passive: true });

    function onPointerDown(ev) {
      isDragging = true;
      scrollbar.classList.add('size-guide__scrollbar--dragging');
      dragStartX = ev.clientX || (ev.touches && ev.touches[0].clientX) || 0;
      const style = window.getComputedStyle(thumb);
      const matrix = new DOMMatrixReadOnly(style.transform);
      thumbStartLeft = matrix.m41 || 0;
      ev.preventDefault();
    }

    function onPointerMove(ev) {
      if (!isDragging) return;
      const clientX = ev.clientX || (ev.touches && ev.touches[0].clientX) || 0;
      const delta = clientX - dragStartX;
      setScrollFromThumb(thumbStartLeft + delta);
    }

    function onPointerUp() {
      if (!isDragging) return;
      isDragging = false;
      scrollbar.classList.remove('size-guide__scrollbar--dragging');
    }

    track.addEventListener('mousedown', function (ev) {
      if (ev.target === thumb) return;
      const rect = track.getBoundingClientRect();
      const left = ev.clientX - rect.left - thumb.clientWidth / 2;
      setScrollFromThumb(left);
    });

    thumb.addEventListener('mousedown', onPointerDown);
    window.addEventListener('mousemove', onPointerMove, { passive: false });
    window.addEventListener('mouseup', onPointerUp, { passive: true });

    thumb.addEventListener('touchstart', onPointerDown, { passive: false });
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('touchend', onPointerUp, { passive: true });
    window.addEventListener('touchcancel', onPointerUp, { passive: true });

    window.addEventListener('resize', updateThumb);
    const mq = window.matchMedia('(max-width: 749px)');
    if (mq && mq.addEventListener) mq.addEventListener('change', updateThumb);

    requestAnimationFrame(updateThumb);
    setTimeout(updateThumb, 200);
  }

  // --- Bra size calculator (ported from product-info.js, kept identical) ---
  function calculateBraSize({ underbust, bust }) {
    if (typeof underbust !== 'number' || typeof bust !== 'number') {
      throw new TypeError('Selected measurements must be numbers');
    }
    let uIn = underbust;
    const bIn = bust;
    uIn = uIn % 2 === 0 ? uIn + 4 : uIn + 5;
    if (uIn <= 0 || bIn <= 0) {
      throw new RangeError('Selected measurements must be positive numbers');
    }
    const sizes = {
      XS: { measurement: ['30-30', '30-31', '32-32'], answerSize: 'Extra Small (XS)' },
      S: { measurement: ['30-32', '30-33', '32-33', '32-34', '34-34'], answerSize: 'Small (S)' },
      M: { measurement: ['30-34', '32-35', '34-35', '34-36', '36-36', '36-37', '38-38'], answerSize: 'Medium (M)' },
      L: { measurement: ['32-36', '32-37', '34-37', '34-38', '36-38', '36-39', '38-39', '38-40', '40-40', '40-41', '42-42'], answerSize: 'Large (L)' },
      XL: { measurement: ['34-39', '36-40', '36-41', '38-41', '38-42', '40-42', '40-43', '42-43', '42-44', '44-45'], answerSize: 'Extra Large (XL)' },
      '2XL': { measurement: ['38-43', '40-44', '40-45', '42-45', '42-46', '44-46', '44-47', '46-48'], answerSize: '2XL' },
      '3XL': { measurement: ['42-47', '44-48', '46-49', '48-50'], answerSize: '3XL' },
      '4XL': { measurement: ['44-49', '46-50', '48-51', '50-52'], answerSize: '4XL' },
      '5XL': { measurement: ['46-51', '48-52'], answerSize: '5XL' },
      '6XL': { measurement: ['48-53'], answerSize: '6XL' },
    };
    const cup = `${uIn}-${bIn}`;
    let size = Object.keys(sizes).find((sz) => sizes[sz].measurement.includes(cup));
    size = sizes[size]?.answerSize;
    const found = Boolean(size);

    if (!found) {
      size = `${window.calculatorMessageNotFound || 'Not found'}`;
    }

    window.dataLayer =  window.dataLayer || [];             
    window.dataLayer.push({
        'event': 'sizeCalculatorResult',
        'event_label': `${size}`,
        'event_category': `${underbust}`,
        'event_action': `${bust}`
    });


    return { size, found };
  }

  function runCalculator(button) {
    const calc = button.closest('size-calculator');
    if (!calc) return;
    const firstEl = calc.querySelector('[data-calculator-first-option]');
    const secondEl = calc.querySelector('[data-calculator-second-option]');
    if (!firstEl || !secondEl) return;

    const result = calculateBraSize({ underbust: +firstEl.value, bust: +secondEl.value });
    const isMobile = window.innerWidth < 750;
    const desktopWrap = calc.querySelector('.ProductTabs-fittingModal-calculator-message.hidden-on-mobile');
    const mobileWrap = calc.querySelector('.ProductTabs-fittingModal-calculator-message.hidden-on-desktop');
    if (desktopWrap) desktopWrap.style.display = isMobile ? 'none' : 'block';
    if (mobileWrap) mobileWrap.style.display = isMobile ? 'block' : 'none';

    const messages = calc.querySelectorAll('[data-calculator-message]');
    const notFound = calc.querySelectorAll('[data-calculator-not-found-message]');
    const sizeEls = calc.querySelectorAll('[data-calculator-size]');

    if (!result.found) {
      messages.forEach((m) => (m.style.display = 'none'));
      notFound.forEach((m) => (m.style.display = 'block'));
      sizeEls.forEach((el) => (el.textContent = ''));
      return;
    }
    messages.forEach((m) => (m.style.display = 'block'));
    notFound.forEach((m) => (m.style.display = 'none'));
    sizeEls.forEach((el) => (el.textContent = result.size));
  }

  // --- Delegated event handling (survives AJAX snippet re-render) ---
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('.js-bundle-size-guide-trigger');
    if (trigger) {
      e.preventDefault();
      openModal(trigger);
      return;
    }

    const calcButton = e.target.closest('[data-calculator-get-size]');
    if (calcButton && calcButton.closest('.ProductTabs-fittingModal')) {
      runCalculator(calcButton);
      return;
    }

    // Close: explicit close button / overlay, or any click outside the modal content.
    const openInModal = e.target.closest('.ProductTabs-fittingModal');
    if (openInModal) {
      if (e.target.closest('.js-fitguideclose') || !e.target.closest('.ProductTabs-fittingModal-content')) {
        closeModal(openInModal);
      }
    }
  });
})();
